// import the api endpoints
import i18n from '@/locales/i18n'
import detectionApi from '@/services/detection.service'
import type { PaginationDataType, PaginationType } from '@/types/PaginationType'
import { EHandler } from '@/types/enum/HandlerEnum'
import { EPaginationOptions } from '@/types/enum/PaginationOptionsEnum'
import type { DetectionModel } from '@/types/models/DetectionModel'
import { getPageSize } from '@/utils/routeUtils'
import { ref, type Ref } from 'vue'
import type { Commit, Dispatch } from 'vuex'
import {
    DETECTION_ROUTE,
    PRESENCE_ROUTE,
    TRIPS_PERSONS_ROUTE,
    TRIPS_SHUTTLES_ROUTE
} from '../../router/routes'
import type { LatestDetectionsParams } from '../../types/models/LatestDetectionsParams'
import { EAlertActions } from '../alertStore/AlertStoreTypes'
import { EStoreModules } from '../storeType'
import { EDetectionActions, EDetectionMutations, type DetectionState } from './DetectionStoreTypes'

const state: Ref<PaginationDataType<DetectionState>> = ref({
    data: { detections: [] },

    current_page: 1,
    last_page: 0,
    total: 0,
    to: 0,
    from: 0,
    per_page: getPageSize() ?? EPaginationOptions.DEFAULT,

    isLoading: false
})

const getters = {
    getDetections: () => state.value.data.detections
}

const actions = {
    async [EDetectionActions.FETCH_DETECTIONS](
        { commit }: { commit: Commit },
        params: Partial<PaginationType>
    ) {
        commit(EDetectionMutations.SET_LOADING, true)
        return detectionApi
            .getDetections(params)
            .then(data => {
                commit(EDetectionMutations.SET_DETECTIONS, data.data)
                commit(EDetectionMutations.SET_TOTAL, data)
                return data.data
            })
            .finally(() => commit(EDetectionMutations.SET_LOADING, false))
    },

    async [EDetectionActions.FETCH_PRESENCES](
        { commit }: { commit: Commit },
        params: Partial<PaginationType>
    ) {
        commit(EDetectionMutations.SET_LOADING, true)
        return detectionApi
            .getPresences(params)
            .then(data => {
                commit(EDetectionMutations.SET_DETECTIONS, data.data)
                commit(EDetectionMutations.SET_TOTAL, data)
                return data.data
            })
            .finally(() => commit(EDetectionMutations.SET_LOADING, false))
    },

    async [EDetectionActions.FETCH_TRIPS](
        { commit }: { commit: Commit },
        params: Partial<PaginationType>
    ) {
        commit(EDetectionMutations.SET_LOADING, true)
        return detectionApi
            .getTrips(params)
            .then(data => {
                commit(EDetectionMutations.SET_DETECTIONS, data.data)
                commit(EDetectionMutations.SET_TOTAL, data)
                return data.data
            })
            .finally(() => commit(EDetectionMutations.SET_LOADING, false))
    },

    async [EDetectionActions.FETCH_PERSONS_TRIPS](
        { commit }: { commit: Commit },
        params: Partial<PaginationType>
    ) {
        commit(EDetectionMutations.SET_LOADING, true)
        return detectionApi
            .getPersonsTrips(params)
            .then(data => {
                commit(EDetectionMutations.SET_DETECTIONS, data.data)
                commit(EDetectionMutations.SET_TOTAL, data)
                return data.data
            })
            .finally(() => commit(EDetectionMutations.SET_LOADING, false))
    },
    async [EDetectionActions.FETCH_SHUTTLES_TRIPS](
        { commit }: { commit: Commit },
        params: Partial<PaginationType>
    ) {
        commit(EDetectionMutations.SET_LOADING, true)
        return detectionApi
            .getShuttlesTrips(params)
            .then(data => {
                commit(EDetectionMutations.SET_DETECTIONS, data.data)
                commit(EDetectionMutations.SET_TOTAL, data)
                return data.data
            })
            .finally(() => commit(EDetectionMutations.SET_LOADING, false))
    },

    async [EDetectionActions.GET_LATEST_DETECTIONS](
        { commit }: { commit: Commit },
        data: LatestDetectionsParams
    ) {
        commit(EDetectionMutations.SET_LOADING, true)
        return await detectionApi
            .getLatestDetections(data)
            .then(res => res?.data ?? res)
            .finally(() => {
                commit(EDetectionMutations.SET_LOADING, false)
            })
    },

    async [EDetectionActions.EXPORT_DETECTIONS](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        params: { params: Partial<PaginationType> & DetectionModel } & Record<string, any>
    ) {
        commit(EDetectionMutations.SET_LOADING, true)
        const generatedCsvName = `${params.site_name ?? 'all-sites'} ${
            params.poi_name ?? 'ALL-POIs'
        }`
        return detectionApi
            .getDetectionsCsv(params.params, generatedCsvName, DETECTION_ROUTE)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    key: [EDetectionActions.EXPORT_DETECTIONS]
                })
            )
            .finally(() => commit(EDetectionMutations.SET_LOADING, false))
    },

    async [EDetectionActions.EXPORT_PERSONS_TRIPS](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        params: { params: Partial<PaginationType> & DetectionModel } & Record<string, any>
    ) {
        commit(EDetectionMutations.SET_LOADING, true)
        const generatedCsvName = `${params.origin_site_name ?? 'origin'} ${
            params.origin_poi_name ?? 'ALL-POIs'
        } - ${params.destination_site_name ?? 'destination'} ${
            params.destination_poi_name ?? 'ALL-POIs'
        }`
        return detectionApi
            .getDetectionsCsv(params.params, generatedCsvName, TRIPS_PERSONS_ROUTE)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    key: [EDetectionActions.EXPORT_DETECTIONS]
                })
            )
            .finally(() => commit(EDetectionMutations.SET_LOADING, false))
    },
    async [EDetectionActions.EXPORT_SHUTTLES_TRIPS](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        params: { params: Partial<PaginationType> & DetectionModel } & Record<string, any>
    ) {
        commit(EDetectionMutations.SET_LOADING, true)
        const generatedCsvName = `${params.origin_site_name ?? 'origin'} ${
            params.origin_poi_name ?? 'ALL-POIs'
        } - ${params.destination_site_name ?? 'destination'} ${
            params.destination_poi_name ?? 'ALL-POIs'
        }`
        return detectionApi
            .getDetectionsCsv(params.params, generatedCsvName, TRIPS_SHUTTLES_ROUTE)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    key: [EDetectionActions.EXPORT_DETECTIONS]
                })
            )
            .finally(() => commit(EDetectionMutations.SET_LOADING, false))
    },
    async [EDetectionActions.EXPORT_PRESENCES](
        { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
        params: { params: Partial<PaginationType> & DetectionModel } & Record<string, any>
    ) {
        commit(EDetectionMutations.SET_LOADING, true)
        const generatedCsvName = `${params.site_name} ${params.poi_name ?? 'ALL-POIs'}`
        return detectionApi
            .getDetectionsCsv(params.params, generatedCsvName, PRESENCE_ROUTE)
            .then(() =>
                dispatch(EHandler.HANDLE_SUCCESS, {
                    key: [EDetectionActions.EXPORT_DETECTIONS]
                })
            )
            .finally(() => commit(EDetectionMutations.SET_LOADING, false))
    },

    [EHandler.HANDLE_SUCCESS](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.SUCCESS,
                message: i18n.global.t(`detection.api.success.${key}`, {
                    name
                })
            },
            { root: true }
        )
        return { name }
    },
    [EHandler.HANDLE_ERROR](
        { dispatch }: { dispatch: Dispatch },
        { name, key }: { name: string; key: string }
    ) {
        dispatch(
            `${EStoreModules.ALERT}/${EAlertActions.QUEUE_ITEM}`,
            {
                action: EAlertActions.ERROR,
                message: i18n.global.t(`detection.api.error.${key}`, {
                    name
                })
            },
            { root: true }
        )
        return { name }
    }
}

const mutations = {
    [EDetectionMutations.SET_DETECTIONS](_: DetectionState, data: DetectionModel[]) {
        state.value.data.detections = data
    },
    [EDetectionMutations.SET_TOTAL](_: DetectionState, data: any) {
        const meta = data.meta ?? data
        state.value.current_page = meta.current_page
        state.value.total = meta.total
        state.value.last_page = meta.last_page
        state.value.to = meta.to
        state.value.from = meta.from
    },
    [EDetectionMutations.SET_LOADING]: (_: DetectionState, data: any) => {
        state.value.isLoading = data
    }
}

export default {
    namespaced: true,
    state,
    actions,
    getters,
    mutations
}
